import { useState } from "react";
import API from "@/lib/api";
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export default function ChangePasswordCard() {
  const [expanded, setExpanded] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setShowPw(false); setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast({ title: "Password too short", description: "New password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    if (next !== confirm) {
      toast({ title: "Passwords don't match", description: "Please retype your new password.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: current, new_password: next });
      setSuccess(true);
      toast({ title: "Password changed", description: "Use your new password next time you sign in." });
      setTimeout(() => { reset(); setExpanded(false); }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Couldn't change password",
        description: typeof detail === "string" ? detail : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!expanded) {
    return (
      <div className="bg-card border border-border rounded-xl p-4" data-testid="change-password-card">
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center gap-3 text-left"
          data-testid="change-password-open-button"
        >
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Change password</p>
            <p className="text-xs text-muted-foreground">Update the password used to sign in.</p>
          </div>
          <span className="text-sm text-primary font-medium shrink-0">Edit</span>
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="change-password-form">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <KeyRound className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Change password</p>
          <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
        </div>
      </div>

      {success ? (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700" data-testid="change-password-success">
          <CheckCircle2 className="w-4 h-4" /> Password updated successfully
        </div>
      ) : (
        <>
          <div>
            <Label className="text-xs font-medium">Current password</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 h-10"
              required
              autoComplete="current-password"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">New password</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1 h-10"
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Confirm new password</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 h-10"
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
            data-testid="toggle-password-visibility"
          >
            {showPw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPw ? "Hide" : "Show"} passwords
          </button>
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => { reset(); setExpanded(false); }}
              disabled={saving}
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !current || !next || !confirm}
              className="flex-1 rounded-full"
              data-testid="change-password-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
